import { ReactNode, useMemo } from 'react'
import { Text } from 'react-aria-components'
import {
	Address,
	Brand,
	Client,
	Document,
	Expense,
	Location,
	Payment,
	Reservation,
	User,
	Vehicle,
} from '../../shared/types'
import { ObjectTypes, useObjectListAll } from '../api/object'
import { CurrencyFormatter } from './currency-formatter'
import { MultiSelect, MultiSelectProps } from './multi-select'
import { Options } from './select'
import { getUserLabel } from './user-formatter'
import { getVehicleLabel } from './vehicle-formatter'

type Reference<O extends keyof ObjectTypes> = {
	object: O
	label?: ReactNode
	renderOption: (item: ObjectTypes[O]) => ReactNode
	getTextValue: (item: ObjectTypes[O]) => string
}

type Props = {
	referenceTo: string
} & Omit<MultiSelectProps<ObjectTypes[string]>, 'options'>

export function ChildObjects({ referenceTo, ...props }: Props) {
	const { renderOption, getTextValue } = REFERENCES[referenceTo] ?? {}

	const { data } = useObjectListAll(referenceTo)

	const options = useMemo(() => {
		if (!data) return []
		return data?.map(item => ({
			value: item.id,
			label: renderOption ? renderOption(item) : item.name,
			textValue: getTextValue ? getTextValue(item) : item.name,
		}))
	}, [data])

	return <MultiSelect options={options as Options} {...props} />
}

export const REFERENCES = {
	address: {
		object: 'address',
		label: 'Adresa',
		getTextValue: (item: Address) => item.id,
		renderOption: (item: Address) => <Text slot="label">{item.id}</Text>,
	} as Reference<'address'>,
	brand: {
		object: 'brand',
		label: 'Pronajímatel',
		getTextValue: (item: Brand) => item.name,
		renderOption: (brand: Brand) => {
			return <Text slot="label">{brand.name}</Text>
		},
	} as Reference<'brand'>,
	client: {
		object: 'client',
		label: 'Zákazník',
		getTextValue: (item: Client) => item.name,
		renderOption: (item: Client) => {
			return (
				<>
					<Text slot="label" className="block">
						{item.name} <small className="text-xs text-gray-800">({item.id})</small>
					</Text>
					<Text slot="description" className="text-xs text-gray-800">
						IČO: {item.ico ?? '-'}, DIČ: {item.dic ?? '-'}
					</Text>
				</>
			)
		},
	} as Reference<'client'>,
	document: {
		object: 'document',
		label: 'Document',
		getTextValue: (item: Document) => item.name,
		renderOption: (item: Document) => {
			return <Text slot="label">{item.name}</Text>
		},
	} as Reference<'document'>,
	expense: {
		object: 'expense',
		label: 'Výdaj',
		getTextValue: (item: Expense) => item.id,
		renderOption: (item: Expense) => (
			<Text slot="label">
				<CurrencyFormatter value={item.value} />
			</Text>
		),
	} as Reference<'expense'>,
	location: {
		object: 'location',
		label: 'Pobočka',
		getTextValue: (item: Location) => item.name,
		renderOption: (location: Location) => {
			return <Text slot="label">{location.name}</Text>
		},
	} as Reference<'location'>,
	payment: {
		object: 'payment',
		label: 'Přijatý příjem',
		getTextValue: (item: Payment) => item.id,
		renderOption: (item: Payment) => (
			<Text slot="label">
				<CurrencyFormatter value={item.value} />
			</Text>
		),
	} as Reference<'payment'>,
	reservation: {
		object: 'reservation',
		label: 'Rezervace',
		getTextValue: (item: Reservation) => item.id,
		renderOption: (reservation: Reservation) => {
			return <Text slot="label">{reservation.id}</Text>
		},
	} as Reference<'reservation'>,
	user: {
		object: 'user',
		label: 'Uživatel',
		renderOption: (user: User) => {
			return <Text slot="label">{getUserLabel(user)}</Text>
		},
		getTextValue: getUserLabel,
	} as Reference<'user'>,
	vehicle: {
		object: 'vehicle',
		label: 'Vozidlo',
		getTextValue: (item: Vehicle) => item.name,
		renderOption: (vehicle: Vehicle) => {
			return <Text slot="label">{getVehicleLabel(vehicle)}</Text>
		},
	} as Reference<'vehicle'>,
} as const
